import type { Dial } from "./dials";
import { normalizeMusic3Lyrics } from "./tags";

export type MimicTrack = {
  id: string;
  title: string;
  artist: string;
  album?: string;
  year?: number;
  lyrics: string;
  selected: boolean;
};

export type MusicMimic = {
  id: string;
  name: string;
  targetArtist: string;
  era: string;
  sampleTexts: string[];
  tracks: MimicTrack[];
  systemPrompt: string;
  vocabularyBank: string[];
  dials: Dial[];
  analysisNotes?: string;
  version: 1;
};

const MAX_SELECTED = 10;
const SAMPLE_CHARS = 1600;

export function emptyMimic(): MusicMimic {
  return {
    id: `mimic-${Date.now()}`,
    name: "",
    targetArtist: "",
    era: "",
    sampleTexts: ["", ""],
    tracks: [],
    systemPrompt: "",
    vocabularyBank: [],
    dials: [],
    version: 1,
  };
}

export function serializeMimic(m: MusicMimic): string {
  return JSON.stringify({ ...m, kind: "forge.music-mimic.v1" }, null, 2);
}

export function applyPulledTracks(
  m: MusicMimic,
  tracks: Omit<MimicTrack, "selected">[],
  meta?: { artist?: string; era?: string },
): MusicMimic {
  const nextTracks = tracks.map((t, i) => ({ ...t, selected: i < MAX_SELECTED }));
  return {
    ...m,
    targetArtist: meta?.artist || m.targetArtist,
    era: m.era || meta?.era || "",
    tracks: nextTracks,
  };
}

export function selectedTracks(m: MusicMimic): MimicTrack[] {
  return (m.tracks || []).filter((t) => t.selected && t.lyrics.trim());
}

export function selectedSamples(m: MusicMimic): string[] {
  const fromTracks = selectedTracks(m).map((t) => {
    const body = t.lyrics.trim().slice(0, SAMPLE_CHARS);
    return `### ${t.title}\n${body}`;
  });
  if (fromTracks.length) return fromTracks.slice(0, MAX_SELECTED);
  return (m.sampleTexts || [])
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED)
    .map((s) => s.slice(0, SAMPLE_CHARS));
}

export function toggleTrack(m: MusicMimic, id: string): MusicMimic {
  const selectedCount = selectedTracks(m).length;
  return {
    ...m,
    tracks: m.tracks.map((t) => {
      if (t.id !== id) return t;
      if (!t.selected && selectedCount >= MAX_SELECTED) return t;
      return { ...t, selected: !t.selected };
    }),
  };
}

export function setTrackLyrics(m: MusicMimic, id: string, lyrics: string): MusicMimic {
  return {
    ...m,
    tracks: m.tracks.map((t) => (t.id === id ? { ...t, lyrics } : t)),
  };
}

export function setTrackSelected(m: MusicMimic, id: string, selected: boolean): MusicMimic {
  if (selected && selectedTracks(m).length >= MAX_SELECTED && !m.tracks.find((t) => t.id === id)?.selected) {
    return m;
  }
  return {
    ...m,
    tracks: m.tracks.map((t) => (t.id === id ? { ...t, selected } : t)),
  };
}

export function mimicLoaded(m: MusicMimic): boolean {
  return Boolean(
    (m.systemPrompt || "").trim() ||
      (m.targetArtist || "").trim() ||
      (m.name || "").trim() ||
      (m.vocabularyBank || []).length ||
      selectedSamples(m).length,
  );
}

function sketchOneSample(sample: string, n: number): string {
  const { lyrics } = normalizeMusic3Lyrics(sample);
  if (!lyrics.trim()) return "";
  type Sec = { tag: string; wordCounts: number[] };
  const sections: Sec[] = [];
  let cur: Sec | null = null;
  for (const raw of lyrics.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const tag = line.match(/^\[([^\]]+)\]$/);
    if (tag) {
      if (cur) sections.push(cur);
      cur = { tag: tag[1], wordCounts: [] };
      continue;
    }
    if (!cur) cur = { tag: "verse", wordCounts: [] };
    cur.wordCounts.push(line.split(/\s+/).filter(Boolean).length);
  }
  if (cur) sections.push(cur);
  if (!sections.length) return "";
  const seq = sections
    .map((s) => {
      const lines = s.wordCounts.length;
      const avg =
        lines > 0 ? Math.round(s.wordCounts.reduce((a, b) => a + b, 0) / lines) : 0;
      return `[${s.tag}] ${lines} lines ~${avg} words`;
    })
    .join(" → ");
  return `Sample ${n}: ${seq}`;
}

/** Section order + line length from samples. Original words only — no reprinted lines. */
export function sketchMimicForm(samples: string[]): string {
  const rows = (samples || [])
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6)
    .map((s, i) => sketchOneSample(s, i + 1))
    .filter(Boolean);
  if (!rows.length) return "";
  return (
    "FORM FINGERPRINT — match this section order, line counts, and line length. Write NEW words. Never copy sample lines.\n" +
    rows.join("\n")
  );
}

/** Sent to the lyrics writer whenever a mimic is loaded (artist, samples, or learned prompt). */
export function compileMimicLyricBrief(m: MusicMimic): string {
  if (!mimicLoaded(m)) return "";
  const artist = (m.name || m.targetArtist || "").trim();
  const bits: string[] = [];
  if (artist) {
    bits.push(
      `Inspired-by ${artist} (not a voice clone). Match their lyric structure, hook shape, rhyme density, and imagery. Original lyrics only.`,
    );
  }
  if ((m.era || "").trim()) bits.push(`Era: ${m.era.trim()}.`);
  if ((m.systemPrompt || "").trim()) bits.push(`Style instructions:\n${m.systemPrompt.trim()}`);
  const notes = (m.analysisNotes || "").trim();
  if (notes) bits.push(`Notes: ${notes.slice(0, 600)}`);
  if ((m.vocabularyBank || []).length) {
    bits.push(
      `Vocabulary flavor (use cousins, do not paste as lines): ${m.vocabularyBank.slice(0, 24).join(", ")}`,
    );
  }
  const form = sketchMimicForm(selectedSamples(m));
  if (form) bits.push(form);
  return bits.join("\n");
}

export function addUploadedTracks(
  m: MusicMimic,
  songs: { title: string; lyrics: string }[],
): MusicMimic {
  const existing = selectedTracks(m).length;
  const added: MimicTrack[] = songs
    .filter((s) => s.lyrics.trim())
    .map((s, i) => ({
      id: `up-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`,
      title: s.title || `Upload ${i + 1}`,
      artist: m.targetArtist || "Upload",
      lyrics: s.lyrics.trim(),
      selected: existing + i < MAX_SELECTED,
    }));
  if (!added.length) return m;
  return {
    ...m,
    tracks: [...added, ...m.tracks],
  };
}

const CURRENT_KEY = "musicforge.mimic.current.v1";
const SHELF_KEY = "musicforge.mimic.shelf.v1";
const MAX_SHELF = 24;

function asTrack(raw: unknown): MimicTrack | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const lyrics = String(o.lyrics || "");
  const id = String(o.id || "").trim();
  if (!id) return null;
  return {
    id,
    title: String(o.title || "Untitled").trim() || "Untitled",
    artist: String(o.artist || ""),
    album: o.album != null ? String(o.album) : undefined,
    year: typeof o.year === "number" && Number.isFinite(o.year) ? o.year : undefined,
    lyrics,
    selected: o.selected !== false,
  };
}

export function parseMimicJson(raw: unknown): MusicMimic | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.kind != null && o.kind !== "forge.music-mimic.v1") return null;
  const tracks = Array.isArray(o.tracks) ? o.tracks.map(asTrack).filter((t): t is MimicTrack => Boolean(t)) : [];
  const sampleTexts = Array.isArray(o.sampleTexts)
    ? o.sampleTexts.map((s) => String(s ?? "")).slice(0, 8)
    : ["", ""];
  while (sampleTexts.length < 2) sampleTexts.push("");
  const dials = Array.isArray(o.dials)
    ? (o.dials.filter((d) => d && typeof d === "object") as Dial[])
    : [];
  const vocabularyBank = Array.isArray(o.vocabularyBank) ? o.vocabularyBank.map(String).filter(Boolean) : [];
  return {
    id: String(o.id || `mimic-${Date.now()}`),
    name: String(o.name || ""),
    targetArtist: String(o.targetArtist || ""),
    era: String(o.era || ""),
    sampleTexts,
    tracks,
    systemPrompt: String(o.systemPrompt || ""),
    vocabularyBank,
    dials,
    analysisNotes: o.analysisNotes != null ? String(o.analysisNotes) : undefined,
    version: 1,
  };
}

export function mimicLabel(m: MusicMimic): string {
  return (m.name || m.targetArtist || "").trim() || "Untitled mimic";
}

export function loadCurrentMimic(): MusicMimic {
  try {
    const raw = localStorage.getItem(CURRENT_KEY);
    if (!raw) return emptyMimic();
    return parseMimicJson(JSON.parse(raw)) || emptyMimic();
  } catch {
    return emptyMimic();
  }
}

export function saveCurrentMimic(m: MusicMimic): void {
  try {
    localStorage.setItem(CURRENT_KEY, JSON.stringify(m));
  } catch {
    /* quota */
  }
}

export function loadMimicShelf(): MusicMimic[] {
  try {
    const raw = localStorage.getItem(SHELF_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.map(parseMimicJson).filter((m): m is MusicMimic => Boolean(m)).slice(0, MAX_SHELF);
  } catch {
    return [];
  }
}

export function saveMimicShelf(list: MusicMimic[]): boolean {
  try {
    localStorage.setItem(SHELF_KEY, JSON.stringify(list.slice(0, MAX_SHELF)));
    return true;
  } catch {
    return false;
  }
}

export function upsertMimicShelf(list: MusicMimic[], m: MusicMimic): MusicMimic[] {
  if (!mimicLoaded(m)) return list;
  const copy: MusicMimic = { ...m, id: m.id || `mimic-${Date.now()}` };
  return [copy, ...list.filter((x) => x.id !== copy.id)].slice(0, MAX_SHELF);
}

export function mergeMimicShelves(local: MusicMimic[], remote: MusicMimic[]): MusicMimic[] {
  const byId = new Map<string, MusicMimic>();
  for (const m of local) {
    if (mimicLoaded(m)) byId.set(m.id, m);
  }
  for (const m of remote) {
    if (mimicLoaded(m)) byId.set(m.id, m);
  }
  return [...byId.values()].slice(0, MAX_SHELF);
}
